import './App.scss';

import * as Sentry from '@sentry/react';

import App from './App';
import React from 'react';
import { createRoot } from 'react-dom/client';

// The fields listed below are stripped here - not just the ones populated
// today - because "empty today" and "structurally incapable of carrying
// patient data" are different guarantees, and only the second one
// survives a future change to this file without anyone revisiting this
// review. This does not cover every possible carrier - see "Known
// residual" further down for what is deliberately left alone and why.
//
// `request.data`, `extra`, `message`, `logentry`, `tags` and `user` are
// all places arbitrary state ends up if something ever calls
// Sentry.setExtra/setTag/setUser/captureMessage, or an error carries a
// request body. `contexts` is worse: it's an open `Record<string,
// Context>` (node_modules/@sentry/core/build/types/types/context.d.ts),
// so `Sentry.setContext('formSnapshot', values)` would add a brand new
// key this file has never seen. Rather than deny-list `state` (the one
// documented field for a React component's state) and hope nothing else
// gets added, SAFE_CONTEXT_KEYS allow-lists the built-in keys the SDK
// itself populates and drops everything else.
//
// None of the above is populated by this app today - zero calls to
// setExtra/setTag/setUser/setContext/captureMessage/addAttachment
// anywhere in src/, confirmed by grep - so all of this is hardening
// against a future change, not a fix for a live leak.
//
// Console breadcrumbs are dropped too: the default `breadcrumbs`
// integration's `console` instrumentation records console.log/warn/error
// arguments verbatim, so a future debug session doing
// `console.log(formValues)` would otherwise ship straight to Sentry.
// (DOM click/keypress breadcrumbs are left alone - checked against this
// version's actual implementation in
// node_modules/@sentry/browser/build/npm/esm/prod/integrations/breadcrumbs.js,
// `htmlTreeAsString`'s attribute set is hard-coded to id/className/
// aria-label/type/name/title/alt - it never reads `value`, with or
// without configuration - so they carry no patient data to begin with.)
//
// `hint.attachments` is the one place event data can ride along that
// `event` itself can never expose (see `EventHint` in
// node_modules/@sentry/core/build/types/types/event.d.ts), which is why
// both callers below now pass `hint` through instead of just `event`.
// Nothing calls Sentry.addAttachment today, so this is currently a
// no-op - but strip it anyway so a future attachment can't bypass every
// other scrub in this function.
//
// Known residual - carriers this function does NOT strip, on purpose:
// - `event.exception.values[].value` and `.stacktrace` - an error
//   message or its frames can interpolate whatever was in scope when it
//   was thrown.
// - `event.spans[].data` - `browserTracingIntegration()` copies request
//   URLs and query strings into span attributes (`http.query`,
//   `url.full`).
// - non-`console` breadcrumbs (`fetch`/`xhr`/`navigation`), which carry
//   URLs.
// None of these carries patient data today: this app has zero throw
// sites and zero console.* calls, and the only outbound value besides
// Sentry's own instrumentation is GA4's `location.pathname` (no query
// string). But nothing in this function would catch it if that changed.
// Standing consequence: never put a patient value in a thrown error
// message, or in a GA4 event parameter - a GA4 parameter would also be
// copied verbatim into a Sentry transaction span by
// browserTracingIntegration, bypassing every scrub below. Span
// scrubbing is deliberately not added here: this app makes no outbound
// request carrying patient data, and stripping spans would gut Sentry's
// usefulness for no current gain.
const SAFE_CONTEXT_KEYS = new Set([
  // Everything `Contexts` declares (context.d.ts) except `state`, which
  // is the one field designed to hold a React component's state. Any key
  // outside this list - built-in or, more importantly, a future custom
  // one added via Sentry.setContext - is dropped.
  'app',
  'device',
  'os',
  'culture',
  'response',
  'trace',
  'cloud_resource',
  'profile',
  'flags'
]);

function stripSensitiveFields(
  event: Sentry.Event,
  hint: Sentry.EventHint
): void {
  delete event.request?.data;
  delete event.extra;
  delete event.message;
  delete event.logentry;
  delete event.tags;
  delete event.user;

  if (event.contexts) {
    for (const key of Object.keys(event.contexts)) {
      if (!SAFE_CONTEXT_KEYS.has(key)) {
        delete event.contexts[key];
      }
    }
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.filter(
      (breadcrumb) => breadcrumb.category !== 'console'
    );
  }

  delete hint.attachments;
}

// Clinicians type patient biometry into this form - keratometry, anterior
// chamber depth, white-to-white, refraction, date of birth. Sentry has run
// here since 2021 with nothing configured to keep that data out of what it
// sends; every option below is chosen with that in mind.
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: 'https://2e937d9ae4044696992e8d4afba8d9b5@o551236.ingest.sentry.io/5674476',

    // Which build an error came from. Without this every event since 2021
    // lands in one undifferentiated bucket: no "first seen in 1.8.1", no
    // "regressed in 1.8.4", and no way to confirm a fix actually stopped an
    // error in the build that shipped it. Sentry's regression detection and
    // "resolved in next release" workflow key off this field and are inert
    // without it (#95).
    //
    // Version AND commit, not the version alone. `deploy` publishes on every
    // qualifying push to main while semantic-release bumps only for a
    // release-triggering commit, so the version does not uniquely identify a
    // build - that is the whole finding of #94, and tagging Sentry with the
    // version alone would reproduce it inside Sentry. The commit is exact;
    // the version is kept alongside it because a bare SHA is unreadable when
    // triaging.
    //
    // Both halves come from vite.config.ts's `define`. When source maps
    // start being uploaded rather than published (#95 part 2), the release
    // sentry-cli creates MUST be this same string, derived from the same two
    // sources - package.json's version and the short GITHUB_SHA. If they
    // ever diverge, symbolication fails silently.
    release: `${import.meta.env.VITE_APP_VERSION}+${import.meta.env.VITE_APP_COMMIT}`,

    // Constant today, and deliberately explicit rather than left to the
    // SDK's default: this init block only runs under `import.meta.env.PROD`
    // (see the guard above), so the only build that reports to Sentry is the
    // gh-pages one. Stated so that the field exists if a staging deploy is
    // ever added, not because it distinguishes anything today.
    environment: 'production',

    // `new Integrations.BrowserTracing()` (the removed @sentry/tracing v6
    // API) is replaced by this function in v10 - confirmed against this
    // installed package's own
    // node_modules/@sentry/browser/build/npm/types/tracing/browserTracingIntegration.d.ts
    // rather than assumed from an older API, since this has changed across
    // several majors.
    //
    // No session-replay integration is added here, deliberately: replay
    // records a DOM/video reconstruction of the page and would be a far
    // bigger leak of patient data than anything tracing or error reporting
    // captures. Do not add one without re-running the PII review below.
    integrations: [Sentry.browserTracingIntegration()],

    // This sent 100% of transactions since 2021 with no sign that volume
    // was ever needed. Performance monitoring only needs a representative
    // sample to spot regressions, so 0.1 keeps that signal while cutting
    // transaction volume - and the ambient page context each transaction
    // carries - by 90%. Note this samples transactions only: errors are
    // unsampled, and browserSessionIntegration() (a v10 default) still
    // sends a session envelope on 100% of page loads.
    tracesSampleRate: 0.1,

    // `sendDefaultPii: false` is already this SDK's effective default,
    // but it does NOT mean "collect nothing extra" - checked the actual
    // resolution, not just the option's deprecation notice
    // (node_modules/@sentry/core/build/esm/utils/data-collection/
    // defaultPiiToCollectionOptions.js, deprecated in v10 in favour of
    // the finer-grained `dataCollection` option). With this flag false,
    // the SDK still resolves `stackFrameVariables: true`,
    // `frameContextLines: 7`, `graphQL: { document: true, variables:
    // true }`, and deny-list-based (not empty) capture of cookies, HTTP
    // headers and URL query params - only `userInfo`, `genAI` and
    // `databaseQueryData` actually flip to false/off.
    //
    // Those permissive defaults are harmless here only because nothing
    // in this browser-only install has a capture path for them:
    // stack-frame variable capture is Node/inspector-only; frameContextLines
    // only feeds the opt-in `contextLinesIntegration()`, which is absent
    // from `getDefaultIntegrations()`
    // (node_modules/@sentry/browser/build/npm/esm/prod/sdk.js); and
    // httpBodies/urlQueryParams/graphQL are read nowhere in the installed
    // @sentry/browser package. This is "inert today", not "collects
    // nothing" - if `contextLinesIntegration()` or a server-side SDK is
    // ever added, re-review this before doing so.
    sendDefaultPii: false,

    // Defence in depth beyond sendDefaultPii/breadcrumb config: run every
    // outgoing error and transaction through the same scrub, in case a
    // future change ever attaches request data, extra context, a stray
    // tag/user/message, a custom context, an attachment, or a console
    // breadcrumb carrying form state. `hint` is passed through (not just
    // `event`) because attachments live only on the hint - see
    // stripSensitiveFields above.
    beforeSend(event, hint) {
      stripSensitiveFields(event, hint);
      return event;
    },
    beforeSendTransaction(event, hint) {
      stripSensitiveFields(event, hint);
      return event;
    }
  });
}

const container = document.getElementById('root');
if (container) {
  createRoot(container).render(<App />);
} else {
  // The old ReactDOM.render(<App />, document.getElementById('root'))
  // threw "Target container is not a DOM element" when #root was missing
  // or renamed, and Sentry.init above captured it. Preserve that loud
  // failure explicitly - createRoot's `if (container)` guard would
  // otherwise silently swallow the same condition into a blank page with
  // zero telemetry.
  throw new Error('Failed to mount: no DOM element with id "root" was found.');
}
